import { useState } from "react";
import { SocialTemplateService, TemplateForm } from "features/dashboard/domain/services/socialTemplateService";
import { toast } from "react-toastify";

/**
 * useSocialTemplate hook
 *
 * Usage:
 *   const { share, copyToClipboard, generateMessage, getFundraisingOptions, copied, isLoading } = useSocialTemplate();
 *
 *   share("whatsapp", data)
 *   share("instagram-story", data)
 *   share("instagram-dm", data, "username")
 *   share("x-tweet", data)
 *   share("x-dm", data, "username")
 */
export const useSocialTemplate = () => {
  const [copied, setCopied] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const copyToClipboard = async (data: TemplateForm) => {
    setIsLoading(true);
    try {
      const message = SocialTemplateService.generateMessage(data);
      const success = await SocialTemplateService.copyToClipboard(message);
      
      if (success) {
        setCopied(true);
        // Reset copied state after 2 seconds
        setTimeout(() => {
          setCopied(false);
        }, 2000);
      } else {
        toast.error("Failed to copy message. Please try again.");
      }
    } catch (error) {
      toast.error("Failed to copy message. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * share - share to a social platform
   * @param {"whatsapp"|"instagram-story"|"instagram-dm"|"x-tweet"|"x-dm"} platform
   * @param {TemplateForm} data
   * @param {string} [username] - required for DMs
   */
  const share = (platform: "whatsapp"|"instagram-story"|"instagram-dm"|"x-tweet"|"x-dm", data: TemplateForm) => {
    const message = SocialTemplateService.generateMessage(data);
    switch (platform) {
      case "whatsapp":
        SocialTemplateService.shareToWhatsApp(message);
        break;
      case "instagram-story":
        SocialTemplateService.shareToInstagramStory(message);
        break;
      case "instagram-dm":
        SocialTemplateService.shareToInstagramUser(message);
        break;
      case "x-tweet":
        SocialTemplateService.shareToX(message);
        break;
      case "x-dm":
        SocialTemplateService.shareToXDM(message);
        break;
      default:
        toast.error("Unsupported platform");
    }
  };

  const generateMessage = (data: TemplateForm) => {
    return SocialTemplateService.generateMessage(data);
  };

  const getFundraisingOptions = () => {
    return SocialTemplateService.getFundraisingOptions();
  };

  return {
    copied,
    isLoading,
    copyToClipboard,
    share,
    generateMessage,
    getFundraisingOptions,
  };
}; 